/*
 * Web Push for the installed app (ToR 3.1(i)): the browser subscribes with the server's VAPID public
 * key and the subscription is kept against this device; the server pushes every alert it delivers
 * in-app (packs issued or updated, meeting reminders, votes opened) to the person's devices.
 */
import { apiClient } from '@/api/apiClient';
import { deviceId } from './device';
import { fromBase64 } from './crypto';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  return fromBase64(padded.replace(/-/g, '+').replace(/_/g, '/'));
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function pushState(): Promise<'unsupported' | 'denied' | 'subscribed' | 'off'> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'subscribed' : 'off';
}

/** Asks for permission, subscribes, and files the subscription against this device. False when the server has no keys or the person refused. */
export async function subscribePush(): Promise<boolean> {
  if (!pushSupported()) return false;
  const { data } = await apiClient.get<{ publicKey: string | null }>('/api/v1/devices/push/public-key');
  if (!data.publicKey) return false;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription() ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.publicKey) });
  await apiClient.put(`/api/v1/devices/${deviceId()}/push`, { subscriptionJson: JSON.stringify(sub.toJSON()) });
  return true;
}

export async function unsubscribePush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) await sub.unsubscribe();
  try { await apiClient.put(`/api/v1/devices/${deviceId()}/push`, { subscriptionJson: null }); } catch { /* offline: the server forgets it at the next contact or wipe */ }
}
